import { X, Database, Server, Key, User, Table, Eye, EyeOff, HelpCircle, Shield, AlertCircle } from "lucide-react";
import { useState } from "react";

const inputCls = "w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1d57a5] focus:border-transparent transition-all duration-200 placeholder:text-slate-400 dark:placeholder:text-slate-500";
const labelCls = "block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2";
const sectionTitleCls = "text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2";

export default function SQLModal({
  isOpen,
  onClose,
  onSubmit,
  editingSql,
  sqlFormData,
  setSqlFormData,
  submitting,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [touched, setTouched] = useState({});

  if (!isOpen) return null;

  const update = (field, value) => {
    setSqlFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleFieldBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const isFieldInvalid = (field, value) => {
    if (!touched[field]) return false;
    if (field === "portNum") {
      const port = parseInt(value);
      return isNaN(port) || port < 1 || port > 65535;
    }
    return !value && field !== "dbPassword" && field !== "schemaName" && field !== "connectionstring";
  };

  const sections = [
    { id: "basic", name: "Basic Configuration", icon: Database },
    { id: "credentials", name: "Credentials", icon: Shield },
    { id: "advanced", name: "Advanced Settings", icon: Server },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl border border-slate-200/50 dark:border-slate-700/50 animate-in zoom-in-95 duration-300">
        
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1d57a5] to-[#0f3061] flex items-center justify-center shadow-lg">
              <Database size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                {editingSql ? "Edit SQL Configuration" : "Add SQL Configuration"}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Configure your database connection settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-200 hover:scale-105"
          >
            <X size={20} className="text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="border-b border-slate-200 dark:border-slate-700 px-6">
          <div className="flex gap-1 overflow-x-auto hide-scrollbar">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`px-4 py-2.5 text-sm font-medium transition-all duration-200 relative ${
                    activeSection === section.id
                      ? "text-[#1d57a5] dark:text-[#4a8ce3]"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={16} />
                    <span>{section.name}</span>
                  </div>
                  {activeSection === section.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#1d57a5] to-[#0f3061] rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={onSubmit} className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            
            {/* Basic Configuration Section */}
            {activeSection === "basic" && (
              <div className="space-y-5 animate-in slide-in-from-right-4 duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Database Type */}
                  <div>
                    <label className={labelCls}>
                      Database Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={sqlFormData.databaseType}
                      onChange={(e) => update("databaseType", e.target.value)}
                      onBlur={() => handleFieldBlur("databaseType")}
                      className={`${inputCls} ${isFieldInvalid("databaseType", sqlFormData.databaseType) ? "border-red-500 focus:ring-red-500" : ""}`}
                      required
                    >
                      <option value="">Select database type</option>
                      <option value="MySQL">MySQL</option>
                      <option value="PostgreSQL">PostgreSQL</option>
                      <option value="SQL Server">SQL Server</option>
                      <option value="Oracle">Oracle</option>
                      <option value="MongoDB">MongoDB</option>
                    </select>
                  </div>

                  {/* Database Name */}
                  <div>
                    <label className={labelCls}>
                      Database Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={sqlFormData.databaseName}
                      onChange={(e) => update("databaseName", e.target.value)}
                      onBlur={() => handleFieldBlur("databaseName")}
                      className={`${inputCls} ${isFieldInvalid("databaseName", sqlFormData.databaseName) ? "border-red-500 focus:ring-red-500" : ""}`}
                      placeholder="my_database"
                      required
                    />
                  </div>

                  {/* Server Name */}
                  <div>
                    <label className={labelCls}>
                      Server/Host <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={sqlFormData.serverName}
                      onChange={(e) => update("serverName", e.target.value)}
                      onBlur={() => handleFieldBlur("serverName")}
                      className={`${inputCls} ${isFieldInvalid("serverName", sqlFormData.serverName) ? "border-red-500 focus:ring-red-500" : ""}`}
                      placeholder="localhost or 192.168.1.100"
                      required
                    />
                  </div>

                  {/* Port */}
                  <div>
                    <label className={labelCls}>
                      Port Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={sqlFormData.portNum}
                      onChange={(e) => update("portNum", e.target.value)}
                      onBlur={() => handleFieldBlur("portNum")}
                      className={`${inputCls} ${isFieldInvalid("portNum", sqlFormData.portNum) ? "border-red-500 focus:ring-red-500" : ""}`}
                      placeholder="5432"
                      required
                    />
                    {isFieldInvalid("portNum", sqlFormData.portNum) && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> Port must be between 1 and 65535
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Credentials Section */}
            {activeSection === "credentials" && (
              <div className="space-y-5 animate-in slide-in-from-right-4 duration-300">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Shield size={18} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">Security Notice</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        Credentials are encrypted and stored securely. Use strong passwords and rotate them regularly.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Username */}
                  <div>
                    <label className={labelCls}>
                      Username <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={sqlFormData.dbUserName}
                        onChange={(e) => update("dbUserName", e.target.value)}
                        onBlur={() => handleFieldBlur("dbUserName")}
                        className={`${inputCls} pl-10 ${isFieldInvalid("dbUserName", sqlFormData.dbUserName) ? "border-red-500 focus:ring-red-500" : ""}`}
                        placeholder="database_user"
                        required
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className={labelCls}>
                      Password {!editingSql && <span className="text-red-500">*</span>}
                      {editingSql && <span className="text-xs font-normal text-slate-400 ml-2">(leave blank to keep current)</span>}
                    </label>
                    <div className="relative">
                      <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={sqlFormData.dbPassword}
                        onChange={(e) => update("dbPassword", e.target.value)}
                        className={`${inputCls} pl-10 pr-10`}
                        placeholder="••••••••"
                        required={!editingSql}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Settings Section */}
            {activeSection === "advanced" && (
              <div className="space-y-5 animate-in slide-in-from-right-4 duration-300">
                {/* Schema */}
                <div>
                  <label className={labelCls}>
                    Schema Name
                    <span className="text-xs font-normal text-slate-400 ml-2">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={sqlFormData.schemaName}
                    onChange={(e) => update("schemaName", e.target.value)}
                    className={inputCls}
                    placeholder="public"
                  />
                </div>

                {/* Connection String */}
                <div>
                  <label className={labelCls}>
                    Connection String
                    <span className="text-xs font-normal text-slate-400 ml-2">(optional override)</span>
                  </label>
                  <textarea
                    value={sqlFormData.connectionstring}
                    onChange={(e) => update("connectionstring", e.target.value)}
                    className={`${inputCls} resize-none font-mono text-sm`}
                    rows="2"
                    placeholder="Server=myserver;Database=mydb;User Id=myuser;Password=mypass;"
                  />
                </div>

                {/* Tables */}
                <div>
                  <label className={labelCls}>
                    <div className="flex items-center gap-2">
                      <Table size={16} />
                      Tables
                      <span className="text-xs font-normal text-slate-400">(comma-separated)</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={sqlFormData.tablesSelected}
                    onChange={(e) => update("tablesSelected", e.target.value)}
                    className={inputCls}
                    placeholder="users, orders, products, categories"
                  />
                  <p className="text-xs text-slate-400 mt-1">Leave empty to include all tables</p>
                </div>

                {/* Views */}
                <div>
                  <label className={labelCls}>
                    <div className="flex items-center gap-2">
                      <Database size={16} />
                      Views
                      <span className="text-xs font-normal text-slate-400">(comma-separated)</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={sqlFormData.viewsSelected}
                    onChange={(e) => update("viewsSelected", e.target.value)}
                    className={inputCls}
                    placeholder="user_summary, order_stats, revenue_report"
                  />
                </div>

                {/* Prompt Configuration */}
                <div>
                  <label className={labelCls}>
                    <div className="flex items-center gap-2">
                      <HelpCircle size={16} />
                      Prompt Configuration
                    </div>
                  </label>
                  <textarea
                    value={sqlFormData.promptConfiguration}
                    onChange={(e) => update("promptConfiguration", e.target.value)}
                    className={`${inputCls} resize-none font-mono text-sm`}
                    rows="3"
                    placeholder="Custom prompt configuration for AI query generation..."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className={labelCls}>
                    Description
                    <span className="text-xs font-normal text-slate-400 ml-2">(optional)</span>
                  </label>
                  <textarea
                    value={sqlFormData.description}
                    onChange={(e) => update("description", e.target.value)}
                    className={`${inputCls} resize-none`}
                    rows="2"
                    placeholder="Brief description of this database connection"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 p-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#1d57a5] to-[#164280] text-white font-medium rounded-xl hover:from-[#164280] hover:to-[#0f3061] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {submitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : (
                editingSql ? "Update Configuration" : "Create Configuration"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}